import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

const KEY_USER    = 'retflow_user';
const KEY_USERS   = 'retflow_users';
const KEY_PENDING = 'retflow_pending_signup';

export const ROLES = { ADMIN: 'admin', WORKER: 'worker', CUSTOMER: 'customer' };

export const generateApiKey = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const seg = (n) => Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `rf_live_${seg(8)}_${seg(16)}_${seg(8)}`;
};

// ── DEFAULT CREDENTIALS ───────────────────────────────────────
// Change these before going live. These are the accounts that
// get created automatically the first time the app loads.
const ADMIN_EMAIL    = 'admin@retflow.ai';
const ADMIN_PASSWORD = 'admin123';
const WORKER_EMAIL    = 'worker@retflow.ai';
const WORKER_PASSWORD = 'worker123';

// ── SEED runs immediately at module load (NOT inside useEffect) ──
// This means the accounts exist before any component mounts,
// so login works on the very first attempt with no delay.
const ensureDefaultAccounts = () => {
  try {
    // Clear any stale data from old "zeno_" keys (previous version)
    if (localStorage.getItem('zeno_users') !== null) {
      localStorage.removeItem('zeno_users');
      localStorage.removeItem('zeno_user');
    }

    const raw = localStorage.getItem(KEY_USERS);
    let users = [];
    try { users = raw ? JSON.parse(raw) : []; } catch { users = []; }

    // Always ensure admin account exists with correct credentials
    const adminIdx = users.findIndex(u => u.id === 'admin_001');
    const admin = {
      id: 'admin_001',
      name: 'Admin',
      email: ADMIN_EMAIL,
      password: ADMIN_PASSWORD,
      role: ROLES.ADMIN,
      plan: 'agency',
      createdAt: '2025-01-01T00:00:00.000Z',
    };
    if (adminIdx === -1) {
      users.push(admin);
    } else {
      // Update credentials in case they were changed here
      users[adminIdx] = { ...users[adminIdx], ...admin };
    }

    // Always ensure worker account exists with correct credentials
    const workerIdx = users.findIndex(u => u.id === 'worker_001');
    const worker = {
      id: 'worker_001',
      name: 'Sales Rep',
      email: WORKER_EMAIL,
      password: WORKER_PASSWORD,
      role: ROLES.WORKER,
      plan: null,
      createdAt: '2025-01-01T00:00:00.000Z',
      quota: { monthly: 10, current: 3 },
      commissionRate: 0.15,
      active: true,
      leads: [],
      totalRevenueClosed: 0,
    };
    if (workerIdx === -1) {
      users.push(worker);
    } else {
      // Preserve leads and other worker data, just update credentials
      users[workerIdx] = {
        ...users[workerIdx],
        email: WORKER_EMAIL,
        password: WORKER_PASSWORD,
        role: ROLES.WORKER,
      };
    }

    localStorage.setItem(KEY_USERS, JSON.stringify(users));
  } catch (e) {
    console.error('[RetFlow] Failed to seed default accounts:', e);
  }
};

// Run immediately when this module is imported — before any React renders
ensureDefaultAccounts();

// ── AUTH PROVIDER ─────────────────────────────────────────────
export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Handle Stripe return
    const params         = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');
    const returnedPlan   = params.get('plan');

    if (checkoutStatus === 'success' && returnedPlan) {
      activatePendingSignup(returnedPlan);
      window.history.replaceState({}, '', '/dashboard');
    } else if (checkoutStatus === 'cancelled') {
      localStorage.removeItem(KEY_PENDING);
      window.history.replaceState({}, '', '/signup');
    } else {
      // Restore existing session
      try {
        const stored = localStorage.getItem(KEY_USER);
        if (stored) {
          const parsed = JSON.parse(stored);
          // Validate the stored session still exists in users list
          const users = JSON.parse(localStorage.getItem(KEY_USERS) || '[]');
          const stillExists = users.find(u => u.id === parsed.id);
          if (stillExists) setUser(parsed);
          else localStorage.removeItem(KEY_USER); // Session invalid, clear it
        }
      } catch (e) {
        localStorage.removeItem(KEY_USER);
      }
    }
    setLoading(false);
  }, []);

  const activatePendingSignup = (plan) => {
    try {
      const pending = JSON.parse(localStorage.getItem(KEY_PENDING) || 'null');
      if (!pending) return;
      const newUser = { ...pending, plan, role: ROLES.CUSTOMER, stripeActivated: true, activatedAt: new Date().toISOString() };
      const users = JSON.parse(localStorage.getItem(KEY_USERS) || '[]');
      if (!users.find(u => u.email === newUser.email)) {
        users.push(newUser);
        localStorage.setItem(KEY_USERS, JSON.stringify(users));
      }
      const { password: _, ...safe } = newUser;
      setUser(safe);
      localStorage.setItem(KEY_USER, JSON.stringify(safe));
      localStorage.removeItem(KEY_PENDING);
    } catch (e) {
      console.error('[RetFlow] Failed to activate pending signup:', e);
    }
  };

  // ── LOGIN — works for all three roles ────────────────────────
  const login = (email, password) => {
    // Re-run seed just in case (safety net)
    ensureDefaultAccounts();

    const raw = localStorage.getItem(KEY_USERS);
    let users = [];
    try { users = raw ? JSON.parse(raw) : []; } catch { users = []; }

    const trimmedEmail = email.trim().toLowerCase();
    const found = users.find(u =>
      u.email.toLowerCase() === trimmedEmail &&
      u.password === password
    );

    if (!found) {
      // Debug helper — logs what accounts exist so you can see what's wrong
      console.warn('[RetFlow Login] Failed. Accounts in storage:', users.map(u => ({ email: u.email, role: u.role })));
      return { error: 'Invalid email or password. Check the browser console for available accounts.' };
    }

    const { password: _, ...safe } = found;
    setUser(safe);
    localStorage.setItem(KEY_USER, JSON.stringify(safe));
    return { success: true, role: safe.role };
  };

  // ── CUSTOMER SIGNUP (goes through Stripe) ────────────────────
  const initiateSignup = async (name, email, password, plan) => {
    const users = JSON.parse(localStorage.getItem(KEY_USERS) || '[]');
    if (users.find(u => u.email.toLowerCase() === email.toLowerCase())) {
      return { error: 'An account with this email already exists.' };
    }

    const pending = {
      id: `usr_${Date.now()}`,
      name, email, password,
      role: ROLES.CUSTOMER,
      plan: 'starter',
      createdAt: new Date().toISOString(),
      bots: [],
      stripeCustomerId: null,
      stripeActivated: false,
    };
    localStorage.setItem(KEY_PENDING, JSON.stringify(pending));

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
    try {
      const res  = await fetch(`${apiUrl}/v1/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, email, userId: pending.id }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || `Server error ${res.status}`);
      window.location.href = data.url;
      return { redirecting: true };
    } catch (err) {
      // Dev bypass — create account directly when backend isn't running
      if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_STRIPE_BYPASS === 'true') {
        console.warn('[RetFlow] Stripe not available — activating account directly (dev mode)');
        const devUser  = { ...pending, plan, stripeActivated: false };
        const allUsers = JSON.parse(localStorage.getItem(KEY_USERS) || '[]');
        if (!allUsers.find(u => u.email === email)) {
          allUsers.push(devUser);
          localStorage.setItem(KEY_USERS, JSON.stringify(allUsers));
        }
        const { password: __, ...safe } = devUser;
        setUser(safe);
        localStorage.setItem(KEY_USER, JSON.stringify(safe));
        localStorage.removeItem(KEY_PENDING);
        return { success: true, devMode: true };
      }
      return { error: `Could not reach payment server: ${err.message}` };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem(KEY_USER);
  };

  const updateUser = (updates) => {
    const updated = { ...user, ...updates };
    setUser(updated);
    localStorage.setItem(KEY_USER, JSON.stringify(updated));
    const users = JSON.parse(localStorage.getItem(KEY_USERS) || '[]');
    const idx = users.findIndex(u => u.id === user.id);
    if (idx !== -1) {
      users[idx] = { ...users[idx], ...updates };
      localStorage.setItem(KEY_USERS, JSON.stringify(users));
    }
  };

  // ── Admin: create worker ─────────────────────────────────────
  const createWorker = (name, email, password, commissionRate = 0.15) => {
    const users = JSON.parse(localStorage.getItem(KEY_USERS) || '[]');
    if (users.find(u => u.email.toLowerCase() === email.toLowerCase())) {
      return { error: 'Email already in use' };
    }
    const worker = {
      id: `worker_${Date.now()}`,
      name, email, password,
      role: ROLES.WORKER,
      plan: null,
      createdAt: new Date().toISOString(),
      quota: { monthly: 10, current: 0 },
      commissionRate,
      active: true,
      leads: [],
      totalRevenueClosed: 0,
    };
    users.push(worker);
    localStorage.setItem(KEY_USERS, JSON.stringify(users));
    return { success: true };
  };

  const getUsersByRole = (role) => {
    const users = JSON.parse(localStorage.getItem(KEY_USERS) || '[]');
    return users.filter(u => u.role === role).map(({ password: _, ...u }) => u);
  };

  const adminUpdateUser = (userId, updates) => {
    const users = JSON.parse(localStorage.getItem(KEY_USERS) || '[]');
    const idx = users.findIndex(u => u.id === userId);
    if (idx === -1) return { error: 'User not found' };
    users[idx] = { ...users[idx], ...updates };
    localStorage.setItem(KEY_USERS, JSON.stringify(users));
    return { success: true };
  };

  const adminDeleteUser = (userId) => {
    const users = JSON.parse(localStorage.getItem(KEY_USERS) || '[]');
    localStorage.setItem(KEY_USERS, JSON.stringify(users.filter(u => u.id !== userId)));
    return { success: true };
  };

  // ── Worker: lead management ──────────────────────────────────
  const workerAddLead = (lead) => {
    const users = JSON.parse(localStorage.getItem(KEY_USERS) || '[]');
    const idx = users.findIndex(u => u.id === user.id);
    if (idx === -1) return { error: 'Worker not found' };
    const newLead = { id: `lead_${Date.now()}`, ...lead, createdAt: new Date().toISOString(), status: lead.status || 'prospect' };
    users[idx].leads = [...(users[idx].leads || []), newLead];
    localStorage.setItem(KEY_USERS, JSON.stringify(users));
    updateUser({ leads: users[idx].leads });
    return { success: true, lead: newLead };
  };

  const workerUpdateLead = (leadId, updates) => {
    const users = JSON.parse(localStorage.getItem(KEY_USERS) || '[]');
    const idx = users.findIndex(u => u.id === user.id);
    if (idx === -1) return { error: 'Worker not found' };
    users[idx].leads = (users[idx].leads || []).map(l =>
      l.id === leadId ? { ...l, ...updates, updatedAt: new Date().toISOString() } : l
    );
    localStorage.setItem(KEY_USERS, JSON.stringify(users));
    updateUser({ leads: users[idx].leads });
    return { success: true };
  };

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, logout, updateUser, initiateSignup,
      createWorker, getUsersByRole, adminUpdateUser, adminDeleteUser,
      workerAddLead, workerUpdateLead,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);